import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  GetUsersResponse,
  PermissionsMetadata,
  SavePermissionsPayload,
  TargetDTO,
  UserDTO,
  UserGrantRow,
} from '../pages/user-access/user-management.model';
import { ApiUrlService } from '../shared/services/api-url.service';
import { ApiService, ApiRoute } from '../shared/constants/api-services.constant';
import { SelectOption } from '@gea/digital-ui-lib';

@Injectable({
  providedIn: 'root',
})
export class UserManagementService {
  private apiUrl: string;

  constructor(
    private http: HttpClient,
    private readonly apiUrlService: ApiUrlService
  ) {
    this.apiUrl = this.apiUrlService.build(ApiService.UserPermissionManagement);
  }

  getUsers(
    sortKey?: string,
    sortOrder?: string,
    page?: number,
    pageSize?: number,
    usernameContains?: string,
    emailContains?: string,
    organizationContains?: string,
    countryContains?: string,
    searchTerm?: string
  ): Observable<GetUsersResponse> {
    let params = new HttpParams();

    if (sortKey) {
      params = params.set('sortBy', sortKey);
    }
    if (sortOrder) {
      params = params.set('sortDir', sortOrder);
    }
    if (page !== undefined) {
      params = params.set('page', page.toString());
    }
    if (pageSize !== undefined) {
      params = params.set('pageSize', pageSize.toString());
    }

    if (usernameContains) {
      params = params.set('userNameContains', usernameContains);
    }
    if (emailContains) {
      params = params.set('emailContains', emailContains);
    }
    if (organizationContains) {
      params = params.set('organizationContains', organizationContains);
    }
    if (countryContains) {
      params = params.set('countryContains', countryContains);
    }

    if (searchTerm) {
      params = params.set('search', searchTerm);
    }

    return this.http.get<GetUsersResponse>(`${this.apiUrl}/${ApiRoute.Users}`, { params });
  }

  getMetadata(userId: string): Observable<PermissionsMetadata> {
    return this.http.get<PermissionsMetadata>(`${this.apiUrl}/${ApiRoute.Permissions}/metadata?targetUserId=${userId}`);
  }

  getUserDetails(userId: string): Observable<UserDTO> {
    return this.http.get<UserDTO>(`${this.apiUrl}/${ApiRoute.Users}/${userId}/${ApiRoute.Permissions}`);
  }

  getTargetsForGrant(grant: string): Observable<TargetDTO> {
    return this.http.get<TargetDTO>(`${this.apiUrl}/target-entities/${grant}`);
  }

  setGrantToUser(grant: SelectOption, targets: SelectOption[], userId: string): Observable<boolean> {
    const payload = {
      grant: grant.name,
      targetIds: targets.map((t) => t.value),
    };
    return this.http.post<boolean>(`${this.apiUrl}/${ApiRoute.Users}/${userId}/${ApiRoute.Grants}`, payload);
  }

  deleteGrants(userId: string, removeGrants: Record<string, string[]>): Observable<void> {
    const payload = { removeGrants };
    return this.http.delete<void>(`${this.apiUrl}/${ApiRoute.Users}/${userId}/${ApiRoute.Grants}`, { body: payload });
  }

  savePermissions(userId: string, payload: SavePermissionsPayload): Observable<boolean> {
    return this.http.put<boolean>(`${this.apiUrl}/${ApiRoute.Users}/${userId}/${ApiRoute.Permissions}`, payload);
  }

  // Helper function
  mapGrantsToArray(grants: Record<string, Record<string, string>>): UserGrantRow[] {
    return Object.entries(grants).reduce((acc: UserGrantRow[], [grantName, targets]: [string, Record<string, string>]) => {
      const rows = Object.entries(targets).map(([targetId, targetName]: [string, string]) => ({
        grant: grantName,
        target: targetName,
        targetId: targetId,
      }));
      return [...acc, ...rows];
    }, []);
  }
}
